/**
 *
 * OrderDetailsPage
 *
 */

import Modal from 'components/Modal/Modal';
import OrderComponent from 'components/Order/Order';
import { Viewport } from 'components/ViewportProvider';
import { selectBuyer, selectUserPermissions } from 'containers/MainLayout/selectors';
import { UnauthorizedComponent } from 'containers/NotAuthorizedPage';
import React, { Fragment } from 'react';
import { Helmet } from 'react-helmet';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch, compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import { css } from 'styles/styled-components';
import translations from 'translations';
import injectReducer from 'utils/injectReducer';
import injectSaga from 'utils/injectSaga';
import utilsMessages from 'utils/messages';
import {
  approveOrder as approveOrderActions,
  cancelOrderSubmit,
  confirmOrderSubmit,
  disapproveOrder as disapproveOrderActions,
  printOrderInvoice as printOrderInvoiceActions,
  reOrderSubmit,
  updateDeliverySubmit,
  updateOrder as updateOrderActions,
} from './actions';
import EditOrderItemMobile from './EditOrderItemMobile';
import messages from './messages';
import OrderDetailPageHeader from './OrderDetailPageHeader';
import OrderItemTable from './OrderItemTable';
import reducer from './reducer';
import saga from './saga';
import selectOrderDetailsPage from './selectors';
import { RootState } from './types';

interface OwnProps {
  orderdetailspage: any;
  buyer: any;
  permissions: string[];
}

interface StateProps {}

interface DispatchProps {
  onCancelOrder: (value: any) => void;
  onApproveOrder: (value: any) => void;
  onDisapproveOrder: (value: any) => void;
  onReOrderAll: (value: any) => void;
  onConfirmOrder: (value: any) => void;
  onUpdateDelivery: (value: any) => void;
  onUpdateOrder: (value: any) => void;
  onPrintOrderInvoice: (value: string) => void;
}

type Props = StateProps & DispatchProps & OwnProps;

const newModalStyle = css`
  header {
    justify-content: center;
    margin-bottom: 24px;
    color: #2f382c;
    font-size: 20px;
    font-weight: 600;
    line-height: 26px;
    font-family: 'SF Pro Text';
  }

  th.ant-table-cell {
    font-size: 16px !important;
    font-weight: 500 !important;
    line-height: 24px !important;
    font-family: 'SF Pro Text' !important;
    color: #808289 !important;
  }

  button.ant-btn-secondary {
    display: flex;
    width: 200px;
    height: 48px;
    padding: 12px 16px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    border-radius: 8px;
    border: 1px solid #d5d7d5;
    background: #fff;
    color: #2f382c;
  }

  button.ant-btn-primary {
    display: flex;
    width: 200px;
    height: 48px;
    padding: 12px 16px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    border-radius: 8px;
    background: #43a047;
    color: #fff;
  }

  .ant-modal-footer {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 40px;
  }
`;

export class OrderDetailsPage extends React.PureComponent<Props> {
  public state: {
    isOpenEditModal: boolean;
    deletedOrderItems: object;
    editedOrderItems: object;
  } = {
    isOpenEditModal: false,
    deletedOrderItems: {},
    editedOrderItems: {},
  };

  public onOpenEditModal = () => {
    this.setState({
      isOpenEditModal: true,
    });
  };

  public onCloseEditModal = () => {
    this.setState({
      isOpenEditModal: false,
      deletedOrderItems: {},
      editedOrderItems: {},
    });
  };

  public onSubmitSaveQuantity = () => {
    this.props.onUpdateOrder({
      orderId: this.props.orderdetailspage.order.id,
      editedOrderItems: this.state.editedOrderItems,
      deletedOrderItems: this.state.deletedOrderItems,
    });
    this.onCloseEditModal();
  };

  public onSetItemQuantity = (data: { itemId: string; quantity: number }) => {
    this.setState({
      editedOrderItems: {
        ...this.state.editedOrderItems,
        [data.itemId]: data.quantity,
      },
    });
  };

  public onDeleteItem = (productId) => {
    this.state.deletedOrderItems[productId] = true;
    this.setState({ deletedOrderItems: { ...this.state.deletedOrderItems } });
  };

  public render() {
    const { isOpenEditModal, editedOrderItems, deletedOrderItems } = this.state;
    const {
      orderdetailspage: { order },
      onCancelOrder,
      onApproveOrder,
      onDisapproveOrder,
      onReOrderAll,
      onConfirmOrder,
      onUpdateDelivery,
      onPrintOrderInvoice,
      permissions,
      buyer,
    } = this.props;

    const isAuthorized = buyer?.id === order?.buyer?.id;
    const isStatusChange = order?.isStatusChange;

    return (
      <Fragment>
        <Helmet>
          <title>{`${translations(messages.header)} | Nhà Cung Cấp Kamereo`}</title>
          <meta name="description" content={`${translations(messages.header)}`} />
        </Helmet>
        {!isAuthorized && !order?.loading ? (
          <UnauthorizedComponent fullHeight={false} hasCTA={false} />
        ) : (
          <>
            <OrderDetailPageHeader
              onPrintOrderInvoice={onPrintOrderInvoice}
              onReOrderAll={onReOrderAll}
              onCancelOrder={onCancelOrder}
              order={order}
              permissions={permissions}
              onApproveOrder={onApproveOrder}
              onDisapproveOrder={onDisapproveOrder}
              onEditOrder={this.onOpenEditModal}
            >
              <FormattedMessage {...messages.header} />
            </OrderDetailPageHeader>
            <OrderComponent
              order={order}
              isStatusChange={isStatusChange}
              onCancelOrder={onCancelOrder}
              onApproveOrder={onApproveOrder}
              onDisapproveOrder={onDisapproveOrder}
              onReOrderAll={onReOrderAll}
              onConfirmOrder={onConfirmOrder}
              onUpdateDelivery={onUpdateDelivery}
              onPrintOrderInvoice={onPrintOrderInvoice}
              permissions={permissions}
            />
            <Modal
              size="large"
              headerLabel={<FormattedMessage {...messages.editOrder} />}
              isOpen={isOpenEditModal}
              onClose={this.onCloseEditModal}
              onSubmit={this.onSubmitSaveQuantity}
              closeLabel={<FormattedMessage {...utilsMessages.cancel} />}
              submitLabel={<FormattedMessage {...utilsMessages.save} />}
              customStyles={newModalStyle}
            >
              <Viewport.Consumer>
                {({ width }) => {
                  if (width > 992) {
                    return (
                      <OrderItemTable
                        items={(order.items || []).filter((item) => !deletedOrderItems[item.product.id])}
                        editedOrderItems={editedOrderItems}
                        onSetQuantity={this.onSetItemQuantity}
                        onDeleteItem={this.onDeleteItem}
                      />
                    );
                  }

                  return (
                    <EditOrderItemMobile
                      items={(order.items || []).map((item) => ({
                        ...item,
                        isDelete: deletedOrderItems[item.product.id],
                      }))}
                      editedItems={editedOrderItems}
                      onSetQuantity={this.onSetItemQuantity}
                      onDeleteItem={this.onDeleteItem}
                    />
                  );
                }}
              </Viewport.Consumer>
            </Modal>
          </>
        )}
      </Fragment>
    );
  }
}

const mapStateToProps = createStructuredSelector<RootState, StateProps>({
  orderdetailspage: selectOrderDetailsPage(),
  permissions: selectUserPermissions(),
  buyer: selectBuyer(),
});

function mapDispatchToProps(dispatch: Dispatch): DispatchProps {
  return {
    onCancelOrder: (data) => dispatch(cancelOrderSubmit(data)),
    onApproveOrder: (data) => dispatch(approveOrderActions.request(data)),
    onDisapproveOrder: (data) => dispatch(disapproveOrderActions.request(data)),
    onReOrderAll: (data) => dispatch(reOrderSubmit(data)),
    onUpdateDelivery: (data) => dispatch(updateDeliverySubmit(data)),
    onConfirmOrder: (data) => dispatch(confirmOrderSubmit(data)),
    onUpdateOrder: (data) => dispatch(updateOrderActions.request(data)),
    onPrintOrderInvoice: (data) => dispatch(printOrderInvoiceActions.request(data)),
  };
}

const withConnect = connect(mapStateToProps, mapDispatchToProps);

const withReducer = injectReducer<OwnProps>({
  key: 'orderDetailsPage',
  reducer: reducer,
});

const withSaga = injectSaga<OwnProps>({ key: 'orderDetailsPage', saga: saga });

export default compose(withReducer, withSaga, withConnect)(OrderDetailsPage);
